import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material'
import {
  ArrowBack as BackIcon,
  CloudUpload as UploadIcon,
  Psychology as AnalyzeIcon,
  Check as CheckIcon,
  Publish as PublishIcon,
} from '@mui/icons-material'
import { educationDraftsAPI } from '../services/api'
import FileDropzone from '../components/education/FileDropzone'
import YamlEditor from '../components/education/YamlEditor'
import IngestionProgress from '../components/education/IngestionProgress'

const STEPS = ['Upload Sources', 'Review Structure', 'Ingest Content', 'Complete']

// Statuses that should show the ingestion progress view
const INGESTION_STATUSES = ['ingesting', 'ingestion_failed']
const COMPLETED_STATUSES = ['completed', 'published']
const ANALYSIS_STATUSES = ['analyzing', 'analysis_failed']

function EducationModuleBuilder() {
  const navigate = useNavigate()
  const { draftId } = useParams()
  const [draft, setDraft] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [validationResult, setValidationResult] = useState(null)
  const pollingRef = useRef(null)

  // Determine current step based on draft status
  const getCurrentStep = useCallback((status) => {
    if (!status) return 0
    if (['uploading', 'uploaded'].includes(status)) return 0
    if (['analyzing', 'analysis_failed', 'draft_ready', 'editing'].includes(status)) return 1
    if (INGESTION_STATUSES.includes(status)) return 2
    if (COMPLETED_STATUSES.includes(status)) return 3
    return 0
  }, [])

  const fetchDraft = useCallback(async () => {
    if (!draftId) return
    try {
      const data = await educationDraftsAPI.getById(draftId)
      setDraft(data)
      setError(null)
    } catch (err) {
      console.error('Error fetching draft:', err)
      setError(err.response?.data?.error || 'Failed to load draft')
    } finally {
      setLoading(false)
    }
  }, [draftId])

  const pollStatus = useCallback(async () => {
    if (!draftId) return
    try {
      const status = await educationDraftsAPI.getStatus(draftId)
      setDraft(prev => ({ ...prev, ...status }))

      // Stop polling if we've reached a terminal state or ready state
      if (['draft_ready', 'completed', 'published', 'analysis_failed', 'ingestion_failed'].includes(status.status)) {
        if (pollingRef.current) {
          clearInterval(pollingRef.current)
          pollingRef.current = null
        }
        // Fetch full draft for updated content after a brief delay
        // to ensure the UI shows the completed state before refreshing
        setTimeout(() => fetchDraft(), 500)
      }
    } catch (err) {
      console.error('Error polling status:', err)
    }
  }, [draftId, fetchDraft])

  // Start polling when in processing states
  useEffect(() => {
    if (draft && ['analyzing', 'ingesting'].includes(draft.status)) {
      if (!pollingRef.current) {
        pollingRef.current = setInterval(pollStatus, 2000)
      }
    } else if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
      }
    }
  }, [draft?.status, pollStatus])

  useEffect(() => {
    fetchDraft()
  }, [fetchDraft])

  const handleFilesUploaded = async (files) => {
    try {
      setActionLoading(true)
      const result = await educationDraftsAPI.uploadFiles(draftId, files)
      setDraft(prev => ({ ...prev, ...result }))
      await fetchDraft()
    } catch (err) {
      setError('Failed to upload files: ' + (err.response?.data?.error || err.message))
    } finally {
      setActionLoading(false)
    }
  }

  const handleRemoveFile = async (fileName) => {
    try {
      await educationDraftsAPI.removeFile(draftId, fileName)
      await fetchDraft()
    } catch (err) {
      setError('Failed to remove file: ' + (err.response?.data?.error || err.message))
    }
  }

  const handleStartAnalysis = async () => {
    try {
      setActionLoading(true)
      setError(null)
      await educationDraftsAPI.startAnalysis(draftId)
      await fetchDraft()
    } catch (err) {
      setError('Failed to start analysis: ' + (err.response?.data?.error || err.message))
    } finally {
      setActionLoading(false)
    }
  }

  const handleSaveYaml = async (yamlContent) => {
    try {
      await educationDraftsAPI.saveYaml(draftId, yamlContent)
      setDraft(prev => ({ ...prev, edited_yaml: yamlContent }))
    } catch (err) {
      setError('Failed to save YAML: ' + (err.response?.data?.error || err.message))
    }
  }

  const handleValidateYaml = async () => {
    try {
      const result = await educationDraftsAPI.validateYaml(draftId)
      setValidationResult(result)
      return result
    } catch (err) {
      setError('Failed to validate YAML: ' + (err.response?.data?.error || err.message))
      return null
    }
  }

  const handleStartIngestion = async () => {
    try {
      setActionLoading(true)
      setError(null)
      await educationDraftsAPI.startIngestion(draftId)
      await fetchDraft()
    } catch (err) {
      setError('Failed to start ingestion: ' + (err.response?.data?.error || err.message))
    } finally {
      setActionLoading(false)
    }
  }

  const handlePublish = async () => {
    try {
      setActionLoading(true)
      setError(null)
      await educationDraftsAPI.publish(draftId)
      await fetchDraft()
    } catch (err) {
      setError('Failed to publish module: ' + (err.response?.data?.error || err.message))
    } finally {
      setActionLoading(false)
    }
  }

  const handleRetry = async () => {
    if (draft.status === 'analysis_failed') {
      await handleStartAnalysis()
    } else if (draft.status === 'ingestion_failed') {
      await handleStartIngestion()
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!draft) {
    return (
      <Box>
        <Alert severity="error">Draft not found</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/education-modules')} sx={{ mt: 2 }}>
          Back to Education Modules
        </Button>
      </Box>
    )
  }

  const currentStep = getCurrentStep(draft.status)

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/education-modules')}
          variant="outlined"
        >
          Back
        </Button>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          Education Module Builder
        </Typography>
        <Typography variant="body2" color="text.secondary" fontFamily="monospace">
          {draft.draft_id}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stepper */}
      <Stepper activeStep={currentStep} sx={{ mb: 4 }}>
        {STEPS.map((label, index) => (
          <Step key={label} completed={index < currentStep}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Step Content */}
      <Paper sx={{ p: 3 }}>
        {/* Step 1: Upload Sources */}
        {currentStep === 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Upload Source Documents
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Upload PDF, DOCX, TXT, or Markdown files containing educational content.
              The AI will analyze these documents and generate a structured module outline.
            </Typography>

            <FileDropzone
              files={draft.source_files || []}
              onFilesAdded={handleFilesUploaded}
              onFileRemoved={handleRemoveFile}
              loading={actionLoading}
            />

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={actionLoading ? <CircularProgress size={18} color="inherit" /> : <AnalyzeIcon />}
                onClick={handleStartAnalysis}
                disabled={actionLoading || !draft.source_files?.length || draft.status === 'analyzing'}
              >
                {draft.status === 'analyzing' ? 'Analyzing...' : 'Analyze Content'}
              </Button>
            </Box>
          </Box>
        )}

        {/* Step 2: Review Structure */}
        {currentStep === 1 && (
          <Box>
            {ANALYSIS_STATUSES.includes(draft.status) && draft.status !== 'analysis_failed' ? (
              <IngestionProgress
                status={draft.status}
                progress={draft.progress_percent}
                currentStep={draft.current_step}
                error={draft.error_message}
                onRetry={handleRetry}
              />
            ) : (
              <>
                <Typography variant="h6" gutterBottom>
                  Review Module Structure
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Review and edit the generated module structure. The YAML defines chapters,
                  lessons, learning objectives, and quiz-worthy points.
                </Typography>

                <YamlEditor
                  yaml={draft.edited_yaml || draft.generated_yaml || ''}
                  onSave={handleSaveYaml}
                  onValidate={handleValidateYaml}
                  validationResult={validationResult}
                />

                <Divider sx={{ my: 3 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Button
                    variant="outlined"
                    onClick={handleStartAnalysis}
                    disabled={actionLoading}
                  >
                    Re-analyze
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={actionLoading ? <CircularProgress size={18} color="inherit" /> : <UploadIcon />}
                    onClick={handleStartIngestion}
                    disabled={actionLoading || !draft.generated_yaml}
                  >
                    Proceed to Ingest
                  </Button>
                </Box>
              </>
            )}
          </Box>
        )}

        {/* Step 3: Ingestion Progress */}
        {currentStep === 2 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Content Ingestion
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Creating knowledge base and uploading documents to Dify for RAG retrieval.
            </Typography>

            <IngestionProgress
              status={draft.status}
              progress={draft.progress_percent}
              currentStep={draft.current_step}
              error={draft.error_message}
              onRetry={handleRetry}
            />
          </Box>
        )}

        {/* Step 4: Complete */}
        {currentStep === 3 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Module Created Successfully!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {draft.status === 'published'
                ? 'Your module has been published and is available for use.'
                : 'Your module is ready. Publish it to make it available for use.'}
            </Typography>

            {draft.module_id && (
              <Typography variant="body2" fontFamily="monospace" sx={{ mb: 3 }}>
                Module ID: {draft.module_id}
              </Typography>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              {draft.status === 'completed' && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={actionLoading ? <CircularProgress size={18} color="inherit" /> : <PublishIcon />}
                  onClick={handlePublish}
                  disabled={actionLoading}
                >
                  Publish Module
                </Button>
              )}
              <Button variant="outlined" onClick={() => navigate('/education-modules')}>
                Back to Education Modules
              </Button>
              {draft.module_id && (
                <Button
                  variant="outlined"
                  onClick={() => navigate(`/education-modules/${draft.module_id}`)}
                >
                  View Module
                </Button>
              )}
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  )
}

export default EducationModuleBuilder
